import React, { useState, useEffect } from 'react';
import { micrositeAPI, projectAPI } from '../../lib/api';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  Plus, Globe, Eye, Edit, Send, Check, ExternalLink, Trash2,
  Layout, Users, BarChart3, Settings
} from 'lucide-react';

const MicrositesPage = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [microsites, setMicrosites] = useState([]);
  const [loading, setLoading] = useState(true);

  const isPartner = hasRole(['partner', 'sub_user']);
  const canApprove = hasRole(['builder_admin', 'marketing_team']);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const micrositesRes = await micrositeAPI.list();
      setMicrosites(micrositesRes.data);
    } catch (error) {
      toast.error('Failed to load microsites');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForApproval = async (id) => {
    try {
      await micrositeAPI.submitForApproval(id);
      toast.success('Submitted for approval');
      loadData();
    } catch (error) {
      toast.error('Failed to submit');
    }
  };

  const handleApprove = async (id) => {
    try {
      await micrositeAPI.approve(id);
      toast.success('Microsite approved and published');
      loadData();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      'draft': { class: 'bg-gray-100 text-gray-700', label: 'Draft' },
      'pending_approval': { class: 'bg-amber-100 text-amber-700', label: 'Pending Approval' },
      'approved': { class: 'bg-blue-100 text-blue-700', label: 'Approved' },
      'published': { class: 'bg-emerald-100 text-emerald-700', label: 'Published' },
    };
    return configs[status] || configs.draft;
  };

  const getBasePath = () => {
    if (user?.role === 'builder_admin') return '/builder';
    if (user?.role === 'marketing_team') return '/marketing';
    return '/partner';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="microsites-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="h-7 w-7 text-violet-600" />
            Microsites
          </h1>
          <p className="text-muted-foreground">
            {isPartner ? 'Create landing pages for your projects' : 'Review and approve partner microsites'}
          </p>
        </div>
        
        {isPartner && (
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => navigate('/partner/microsites/domains')}
              data-testid="btn-domain-management"
            >
              <Settings className="h-4 w-4 mr-2" />
              Domain Settings
            </Button>
            <Button 
              onClick={() => navigate(`${getBasePath()}/microsites/new`)}
              data-testid="btn-create-microsite"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Microsite
            </Button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Layout className="h-4 w-4" />
              <span className="text-sm">Total</span>
            </div>
            <p className="text-2xl font-bold">{microsites.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <Globe className="h-4 w-4" />
              <span className="text-sm">Published</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700">
              {microsites.filter(m => m.status === 'published').length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <Eye className="h-4 w-4" />
              <span className="text-sm">Total Views</span>
            </div>
            <p className="text-2xl font-bold">
              {microsites.reduce((sum, m) => sum + (m.analytics?.views || 0), 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Users className="h-4 w-4" />
              <span className="text-sm">Total Leads</span>
            </div>
            <p className="text-2xl font-bold">
              {microsites.reduce((sum, m) => sum + (m.analytics?.leads || 0), 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Microsites Grid */}
      {microsites.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Globe className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No microsites yet</p>
            <p className="text-muted-foreground mb-4">
              {isPartner ? 'Create your first microsite to promote projects' : 'No microsites pending approval'}
            </p>
            {isPartner && (
              <Button onClick={() => navigate(`${getBasePath()}/microsites/new`)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Microsite
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {microsites.map((site) => {
            const statusConfig = getStatusConfig(site.status);
            return (
              <Card key={site.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                {/* Preview Thumbnail */}
                <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Globe className="h-12 w-12 text-slate-300" />
                  </div>
                  <Badge className={`absolute top-3 right-3 ${statusConfig.class}`}>
                    {statusConfig.label}
                  </Badge>
                </div>
                
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg truncate">{site.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">/{site.slug}</p>
                  
                  {site.description && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {site.description}
                    </p>
                  )}
                  
                  {/* Analytics */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4 border-t pt-3">
                    <span className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      {site.analytics?.views || 0} views
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {site.analytics?.leads || 0} leads
                    </span>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    {site.status === 'draft' && isPartner && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => navigate(`${getBasePath()}/microsites/${site.id}/edit`)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handleSubmitForApproval(site.id)}
                          data-testid={`btn-submit-${site.id}`}
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Submit
                        </Button>
                      </>
                    )}
                    
                    {site.status === 'pending_approval' && canApprove && (
                      <Button 
                        size="sm" 
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleApprove(site.id)}
                        data-testid={`btn-approve-${site.id}`}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approve & Publish
                      </Button>
                    )}
                    
                    {site.status === 'published' && (
                      <Button variant="outline" size="sm" className="w-full" asChild>
                        <a href={`/m/${site.slug}`} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View Live
                        </a>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MicrositesPage;
